<script>
  import { createEventDispatcher } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faPlus, faTimes } from '@fortawesome/pro-light-svg-icons';
  
  const dispatch = createEventDispatcher();
  
  export let formConfig = {};
  export let formData = {};
  export let formErrors = {};
  export let addableFieldGroups = {};
  export let isEditing = false;
  
  function addFieldGroup(sectionName) {
    if (!addableFieldGroups[sectionName]) {
      addableFieldGroups[sectionName] = { groups: [] };
    }
    
    const newIndex = Math.max(...addableFieldGroups[sectionName].groups, -1) + 1;
    addableFieldGroups[sectionName].groups = [...addableFieldGroups[sectionName].groups, newIndex];
    
    // Initialize form data for the new group
    const section = addableFieldGroups[sectionName].section;
    if (section && section.sections) {
      section.sections.forEach(componentSection => {
        if (componentSection.type === 'component' && componentSection.component) {
          const component = componentSection.component;
          const fieldName = `${component.name}[${newIndex}]`;
          formData[fieldName] = '';
        }
      });
    }
  }
  
  function removeFieldGroup(sectionName, groupIndex) {
    if (!addableFieldGroups[sectionName]) return;
    
    const currentGroups = addableFieldGroups[sectionName].groups;
    const groupIndexToRemove = currentGroups.indexOf(groupIndex);
    
    if (groupIndexToRemove > -1) {
      addableFieldGroups[sectionName].groups = currentGroups.filter(g => g !== groupIndex);
      
      // Remove form data for this group
      const section = addableFieldGroups[sectionName].section;
      if (section && section.sections) {
        section.sections.forEach(componentSection => {
          if (componentSection.type === 'component' && componentSection.component) {
            const component = componentSection.component;
            const fieldName = `${component.name}[${groupIndex}]`;
            delete formData[fieldName];
          }
        });
      }
    }
  }
  
  function renderFormFields(section) {
    const fields = [];
    
    if (!section || !section.sections) return fields;

    section.sections.forEach(subSection => {
      if (subSection.type === 'component' && subSection.component) {
        const component = subSection.component;
        const field = {
          name: component.name,
          header: component.header,
          type: getFieldType(component.componentName),
          placeholder: component.placeholder,
          required: component.validations?.some(v => v.required) || false,
          options: component.configuration?.options || [],
          rank: component.rank || null
        };
        fields.push(field);
      } else if (subSection.type === 'sections' && subSection.sectionType === 'addableFields') {
        const addableField = {
          type: 'addableFields',
          sectionName: subSection.sectionName,
          header: subSection.header,
          section: subSection
        };
        fields.push(addableField);
      } else if (subSection.sections) {
        fields.push(...renderFormFields(subSection));
      }
    });

    return fields;
  }
  
  function getFieldType(componentName) {
    switch (componentName) {
      case 'InputTextComponent':
        return 'text';
      case 'InputNumberComponent':
        return 'number';
      case 'InputDateComponent':
        return 'date';
      case 'InputTextAreaComponent':
        return 'textarea';
      case 'InputDropdownComponent':
        return 'select';
      case 'InputMultiCheckboxesComponent':
        return 'multiCheckbox';
      case 'DTSQQuestionComponent':
        return 'dtsq';
      default:
        return 'text';
    }
  }
  
  // Toggle a checkbox option in multi-checkbox field
  function toggleCheckboxOption(fieldName, optionCode) {
    if (!formData[fieldName]) {
      formData[fieldName] = [];
    }
    
    const currentValues = Array.isArray(formData[fieldName]) ? formData[fieldName] : [];
    const index = currentValues.indexOf(optionCode);
    
    if (index > -1) {
      // Remove the option
      formData[fieldName] = currentValues.filter(v => v !== optionCode);
    } else {
      // Add the option
      formData[fieldName] = [...currentValues, optionCode];
    }
  }
  
  // Check if a checkbox option is selected
  function isCheckboxSelected(fieldName, optionCode) {
    if (!formData[fieldName]) return false;
    const values = Array.isArray(formData[fieldName]) ? formData[fieldName] : [];
    return values.includes(optionCode);
  }
  
  // Check if a field value is empty
  function isFieldEmpty(value, fieldType) {
    if (value === null || value === undefined) return true;
    if (fieldType === 'multiCheckbox') {
      return !Array.isArray(value) || value.length === 0;
    }
    if (typeof value === 'string') return value.trim() === '';
    return false;
  }
  
  // Validate all required fields
  function validateForm() {
    const fields = renderFormFields(formConfig.section);
    let isValid = true;
    const newErrors = {};
    
    fields.forEach(field => {
      if (field.type === 'addableFields') {
        // Validate addable field groups
        if (addableFieldGroups[field.sectionName]) {
          addableFieldGroups[field.sectionName].groups.forEach(groupIndex => {
            field.section.sections.forEach(componentSection => {
              if (componentSection.type === 'component' && componentSection.component) {
                const component = componentSection.component;
                const isRequired = component.validations?.some(v => v.required) || false;
                const fieldName = `${component.name}[${groupIndex}]`;
                const fieldType = getFieldType(component.componentName);
                
                if (isRequired && isFieldEmpty(formData[fieldName], fieldType)) {
                  newErrors[fieldName] = 'This field is required';
                  isValid = false;
                }
              }
            });
          });
        }
      } else if (field.required) {
        if (isFieldEmpty(formData[field.name], field.type)) {
          newErrors[field.name] = 'This field is required';
          isValid = false;
        }
      }
    });
    
    // Update formErrors - clear old errors and set new ones
    Object.keys(formErrors).forEach(key => delete formErrors[key]);
    Object.assign(formErrors, newErrors);
    
    // Trigger reactivity
    formErrors = { ...formErrors };
    
    return isValid;
  }
  
  // Handle submit with validation
  function handleSubmit() {
    if (validateForm()) {
      dispatch('submit');
    }
  }
</script>

<div class="space-y-4">
  {#each renderFormFields(formConfig.section) as field}
    {#if field.type === 'addableFields'}
      <div class="addable-fields-container">
        <div class="flex justify-between items-center mb-3">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {field.header || 'Add Items'}
          </label>
          <button
            type="button"
            on:click={() => addFieldGroup(field.sectionName)}
            class="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} class="w-3 h-3 mr-1" />
            Add Item
          </button>
        </div>
        
        {#if addableFieldGroups[field.sectionName]}
          <div class="space-y-3">
            {#each addableFieldGroups[field.sectionName].groups as groupIndex}
              <div class="addable-field-group border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div class="flex justify-between items-center mb-3">
                  <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Item {groupIndex + 1}
                  </h4>
                  {#if addableFieldGroups[field.sectionName].groups.length > 1}
                    <button
                      type="button"
                      on:click={() => removeFieldGroup(field.sectionName, groupIndex)}
                      class="text-red-500 hover:text-red-700 text-sm"
                    >
                      <FontAwesomeIcon icon={faTimes} class="w-3 h-3" />
                    </button>
                  {/if}
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {#each field.section.sections as componentSection}
                    {#if componentSection.type === 'component' && componentSection.component}
                      {@const component = componentSection.component}
                      {@const fieldName = `${component.name}[${groupIndex}]`}
                      <div class="form-field">
                        <label for={fieldName} class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {component.header}
                          {#if component.validations?.some(v => v.required)}
                            <span class="text-red-500">*</span>
                          {/if}
                        </label>
                        
                        {#if getFieldType(component.componentName) === 'text'}
                          <input
                            type="text"
                            id={fieldName}
                            bind:value={formData[fieldName]}
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-sm"
                            class:border-red-500={formErrors[fieldName]}
                            placeholder={component.placeholder || ''}
                          />
                        {:else if getFieldType(component.componentName) === 'number'}
                          <input
                            type="number"
                            id={fieldName}
                            bind:value={formData[fieldName]}
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-sm"
                            class:border-red-500={formErrors[fieldName]}
                            placeholder={component.placeholder || ''}
                          />
                        {:else if getFieldType(component.componentName) === 'date'}
                          <input
                            type="datetime-local"
                            id={fieldName}
                            bind:value={formData[fieldName]}
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-sm"
                            class:border-red-500={formErrors[fieldName]}
                          />
                        {:else if getFieldType(component.componentName) === 'select'}
                          <select
                            id={fieldName}
                            bind:value={formData[fieldName]}
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-sm"
                            class:border-red-500={formErrors[fieldName]}
                          >
                            <option value="">Select an option</option>
                            {#each component.configuration?.options || [] as option}
                              <option value={option.code}>{option.label}</option>
                            {/each}
                          </select>
                        {/if}
                        
                        {#if formErrors[fieldName]}
                          <p class="text-red-500 text-xs mt-1">{formErrors[fieldName]}</p>
                        {/if}
                      </div>
                    {/if}
                  {/each}
                </div>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    {:else}
      <div class="form-field">
        <label for={field.name} class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {field.header}
          {#if field.required}
            <span class="text-red-500">*</span>
          {/if}
        </label>
      
        {#if field.type === 'text'}
          <input
            type="text"
            id={field.name}
            bind:value={formData[field.name]}
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            class:border-red-500={formErrors[field.name]}
            placeholder={field.placeholder || ''}
          />
        {:else if field.type === 'number'}
          <input
            type="number"
            id={field.name}
            bind:value={formData[field.name]}
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            class:border-red-500={formErrors[field.name]}
            placeholder={field.placeholder || ''}
          />
        {:else if field.type === 'date'}
          <input
            type="datetime-local"
            id={field.name}
            bind:value={formData[field.name]}
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            class:border-red-500={formErrors[field.name]}
          />
        {:else if field.type === 'textarea'}
          <textarea
            id={field.name}
            bind:value={formData[field.name]}
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            class:border-red-500={formErrors[field.name]}
            placeholder={field.placeholder || ''}
          ></textarea>
        {:else if field.type === 'select'}
          <select
            id={field.name}
            bind:value={formData[field.name]}
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            class:border-red-500={formErrors[field.name]}
          >
            <option value="">Select an option</option>
            {#each field.options || [] as option}
              <option value={option.code}>{option.label}</option>
            {/each}
          </select>
        {:else if field.type === 'multiCheckbox'}
          <div class="multi-checkbox-group" class:has-error={formErrors[field.name]}>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-2 {formErrors[field.name] ? 'p-2 border border-red-500 rounded-md' : ''}">
              {#each field.options || [] as option}
                {#if option.isActive !== false}
                  {@const isSelected = Array.isArray(formData[field.name]) && formData[field.name].includes(option.code)}
                  <label
                    class="flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors
                      {isSelected 
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' 
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      on:change={() => toggleCheckboxOption(field.name, option.code)}
                      class="w-4 h-4 rounded border-2 border-gray-400 dark:border-gray-500 bg-transparent checked:bg-blue-600 checked:border-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                    />
                    <span class="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                  </label>
                {/if}
              {/each}
            </div>
          </div>
        {:else if field.type === 'dtsq'}
          <div class="dtsq-question">
            <div class="mb-2">
              <span class="text-sm text-gray-600 dark:text-gray-400">{field.rank?.low || 'Never'}</span>
              <span class="text-sm text-gray-600 dark:text-gray-400 float-right">{field.rank?.high || 'Almost Always'}</span>
            </div>
            <div class="grid grid-cols-5 gap-2">
              {#each field.options || [] as option}
                <button
                  type="button"
                  class="px-3 py-2 border rounded-md text-sm font-medium transition-colors"
                  class:selected={formData[field.name] === option.code}
                  class:border-blue-500={formData[field.name] === option.code}
                  class:bg-blue-50={formData[field.name] === option.code}
                  class:dark:bg-blue-900={formData[field.name] === option.code}
                  class:dark:text-blue-100={formData[field.name] === option.code}
                  class:border-gray-300={formData[field.name] !== option.code}
                  class:dark:border-gray-600={formData[field.name] !== option.code}
                  class:bg-white={formData[field.name] !== option.code}
                  class:dark:bg-gray-700={formData[field.name] !== option.code}
                  class:text-gray-700={formData[field.name] !== option.code}
                  class:dark:text-gray-300={formData[field.name] !== option.code}
                  class:hover:bg-gray-50={formData[field.name] !== option.code}
                  class:dark:hover:bg-gray-600={formData[field.name] !== option.code}
                  on:click={() => formData[field.name] = option.code}
                >
                  {option.label}
                </button>
              {/each}
            </div>
          </div>
        {/if}
        
        {#if formErrors[field.name]}
          <p class="text-red-500 text-sm mt-1">{formErrors[field.name]}</p>
        {/if}
      </div>
    {/if}
  {/each}
</div>

<div class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
  <button
    type="button"
    class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors"
    on:click={() => dispatch('cancel')}
  >
    Cancel
  </button>
  <button
    type="submit"
    class="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
    on:click={handleSubmit}
  >
    {isEditing ? 'Update' : 'Submit'}
  </button>
</div>

<style>
  .dtsq-question {
    margin-bottom: 1rem;
  }

  .dtsq-question .grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 0.5rem;
  }

  .dtsq-question button {
    transition: all 0.2s ease-in-out;
  }

  .dtsq-question button:hover {
    transform: translateY(-1px);
  }

  .dtsq-question button.selected {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
</style>
